import { create } from 'zustand'
import type { HelpVideoId } from '@tarrot/shared'

/**
 * Where a help video was opened from — drives the `from` prop on the
 * `help_video_played` analytics event. `list` = the Help center rows,
 * `hint` = a contextual hint dot (Task 3), `onboarding` = the last
 * onboarding step's optional "watch your first pull" row.
 */
export type HelpFrom = 'list' | 'hint' | 'onboarding'

interface HelpSheetState {
  /** Id of the video whose player sheet is open, or null when closed. */
  openId: HelpVideoId | null
  /** Origin of the current open — carried onto the play analytics event. */
  from: HelpFrom
  /** Open the player sheet for `id`, recording where it was opened from. */
  open: (id: HelpVideoId, from: HelpFrom) => void
  /** Dismiss the sheet. */
  close: () => void
}

/**
 * Cross-component surface for the help video player sheet. Any screen can pop
 * the sheet without prop-drilling — the Help center rows, the onboarding row,
 * and (batch-5 Task 3) contextual hint dots all call `open(id, from)`. A single
 * `<HelpVideoSheet/>` is mounted app-wide (App.tsx) and renders off this store,
 * so it stays a no-op DOM-wise until something opens it.
 *
 * Task 3 usage: `useHelpSheet.getState().open(videoId, 'hint')`, or the hook
 * form `const open = useHelpSheet((s) => s.open)`.
 */
export const useHelpSheet = create<HelpSheetState>((set) => ({
  openId: null,
  from: 'list',
  open: (id, from) => set({ openId: id, from }),
  close: () => set({ openId: null }),
}))
